import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscription } from 'rxjs';
import { EDeviceThermoModel } from 'src/app/conf/device-models';
import { Device } from 'src/app/model/device';
import { Thermo, ThermoBnB, ThermoQnE } from 'src/app/model/thermo';
import { StandardPageLayout } from 'src/app/modules/shared/stdlayoutlib/classes.stdlayoutlib';
import { AppState } from 'src/app/store/app.reducer';
import { DialogService } from 'primeng/dynamicdialog';
import * as DeviceThActions from 'src/app/store/device-th/device-th.actions';
import { selectCurrentDeviceThermo } from 'src/app/store/device-th/device-th.selector';
import { selectCurrentDevice } from 'src/app/store/device/device.selector';
import { selectIsImpersMode } from 'src/app/store/user/user.selector';
import { compareFirmwareVersions } from '../../shared/utils/version-compare';
import { BnB_MINFWVER_THONE230, ButtonsTemperatureSetPoint, SliderTemperatureSetPoint, TH_BB_TEMP_MAX_SETPOINT, TH_BB_TEMP_MIN_SETPOINT, TH_BB_TEMP_STEP, TH_DELTA_STEPS, TH_MANUAL_TEMP_MAX_SETPOINT, TH_MANUAL_TEMP_MIN_SETPOINT, TH_MANUAL_TEMP_STEP, TH_MAX_VALUE_DELTA, TH_MIN_VALUE_DELTA, TH_QE_COURTESY_TIME_MAX_VALUE, TH_QE_COURTESY_TIME_MIN_VALUE, TH_QE_COURTESY_TIME_STEPS, TH_QE_STANDBY_TEMP_MAX_SETPOINT, TH_QE_STANDBY_TEMP_MIN_SETPOINT, TH_QE_STANDBY_TEMP_STEP } from 'src/app/conf/thermo';

@Component({
	selector: 'app-thermo-commands',
	templateUrl: './thermo-commands.component.html',
	providers: [DialogService],
	styles: `
		:host ::ng-deep .p-panel-header {
			border: 1px solid #D5D5D5 !important;
			border-bottom: none !important;
		}
		:host ::ng-deep .p-panel-content {
			border: 1px solid #D5D5D5 !important;
		}
	`
})

export class ThermoCommandsComponent implements OnInit, OnDestroy {
	thermoDevicesSub: Subscription;
	deviceSub: Subscription;

	thermo: Thermo;
	device: Device = new Device();
	thermoForm: FormGroup;

	seasonControlOptions: any[] = [];
	bnbModalityControlOptions: any[] = [];
	thermalControlOptions: any[] = [];

	bnbThermalControl: string = 'manual';
	bnbThermalControlOptions: any[] = [];

	isImpersMode: boolean;
	isImpersMode$: Observable<boolean | undefined>;
	isImpersModeSub: Subscription | undefined;

	spinnerManualTemp: number;
	timerUpdateThermoManual: number = 0;
	thManualTempMin: number = TH_MANUAL_TEMP_MIN_SETPOINT;
	thManualTempMax: number = TH_MANUAL_TEMP_MAX_SETPOINT;
	thManualTempStepsValue: number = TH_MANUAL_TEMP_STEP;

	thMinValueDelta: number = TH_MIN_VALUE_DELTA;
	thMaxValueDelta: number = TH_MAX_VALUE_DELTA;
	thDeltaStepsValue: number = TH_DELTA_STEPS;

	spinnerBnB: number;
	timerUpdateThermoBnb: number = 0;
	thBnBTempMin: number = TH_BB_TEMP_MIN_SETPOINT;
	thBnBTempMax: number = TH_BB_TEMP_MAX_SETPOINT;
	thBnBTempStepsValue: number = TH_BB_TEMP_STEP;

	thQeMinValue: number = TH_QE_COURTESY_TIME_MIN_VALUE;
	thQeMaxValue: number = TH_QE_COURTESY_TIME_MAX_VALUE;
	thQeTimeStepsValue: number = TH_QE_COURTESY_TIME_STEPS;

	spinnerQnEStandby: number;
	timerUpdateThermoQnE: number = 0;
	thQuickEasyTempMin: number = TH_QE_STANDBY_TEMP_MIN_SETPOINT;
	thQuickEasyTempMax: number = TH_QE_STANDBY_TEMP_MAX_SETPOINT;
	thQuickEasyTempStep: number = TH_QE_STANDBY_TEMP_STEP;

	sliderType = SliderTemperatureSetPoint;
	buttonsTempSet = ButtonsTemperatureSetPoint;

	screenWidth: any;

	constructor(
		private store: Store<AppState>,
		private translate: TranslateService,
		public dialogService: DialogService,
	) {
		this.initSelectors();
	}

	initSelectors(): void {
		this.isImpersMode$ = this.store.select(selectIsImpersMode);
	}

	ngOnInit(): void {
		this.deviceSub = this.store.select(selectCurrentDevice).subscribe(
			device => {
				this.device = device;
			}
		);
		this.thermoDevicesSub = this.store.select(selectCurrentDeviceThermo).subscribe(
			deviceThermo => {
				this.thermo = deviceThermo;
				if (this.thermo !== undefined) {
					this.createFormGroup();
				} else {
					this.thermo = null;
				}
			}
		);
		this.isImpersModeSub = this.isImpersMode$?.subscribe(
			(isImpersMode) => {
				this.isImpersMode = isImpersMode;
				if (this.device) {
					if (this.isImpersMode) {
						this.store.dispatch(DeviceThActions.openWebsocketThermo({ userId: this.device.OwnerId }));
						this.store.dispatch(DeviceThActions.getThermoDeviceV2({ keycode: this.device.Keycode, userId: this.device.OwnerId }));
					} else {
						this.store.dispatch(DeviceThActions.openWebsocketThermo({}));
						this.store.dispatch(DeviceThActions.getThermoDeviceV2({ keycode: this.device.Keycode }));
					}
				}
			}
		);
		this.onResize();

		this.translate.onLangChange.subscribe(() => {
			this.contolCommandTh();
		});
	}

	ngOnDestroy(): void {
		this.thermoDevicesSub?.unsubscribe();
		this.deviceSub?.unsubscribe();
		this.isImpersModeSub?.unsubscribe();
		this.store.dispatch(DeviceThActions.closeWebsocketThermo());
	}

	createFormGroup(): void {
		this.spinnerManualTemp = this.thermo.manual_temp;
		if (this.thermo.mode === 'null') {
			this.thermo.mode = 'off';
		}
		if (this.thermo.bb === undefined || this.thermo.bb === null || this.thermo.bb?.enable === undefined || this.thermo.bb?.enable === null) {
			this.thermo.bb = new ThermoBnB();
		}
		if (this.thermo.mode !== 'manual') {
			this.bnbThermalControl = 'off'
		}
		this.spinnerBnB = this.thermo.bb.temp;

		if (this.thermo.qe === undefined || this.thermo.qe === null || this.thermo.qe?.enable === undefined || this.thermo.qe?.enable === null) {
			this.thermo.qe = new ThermoQnE();
		}
		this.spinnerQnEStandby = this.thermo.qe.stdby_temp;

		this.thermoForm = new FormGroup({
			'seasonControl': new FormControl(this.thermo.current_season),
			'bnbModalityControl': new FormControl(this.thermo.bb.enable),
			'thermalControl': new FormControl(this.thermo.mode),
			'bnbThermalControl': new FormControl(this.thermo.mode),
			'temperature': new FormControl(this.thermo.manual_temp),
			'bnbTemperature': new FormControl(this.thermo.bb.temp),
			'bnbDeltaTempPositive': new FormControl(this.thermo.bb.delta_temp_p),
			'bnbDeltaTempNegative': new FormControl(this.thermo.bb.delta_temp_n),
			'qneControl': new FormControl(this.thermo.qe.enable),
			'qneTemperature': new FormControl(this.thermo.qe.stdby_temp),
			'qneCourtesyTime': new FormControl(this.thermo.qe.courtesy_time),
		});
		this.contolCommandTh();
	}

	contolCommandTh(): void {
		this.seasonControlOptions = [
			{ icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF'), value: 'off' },
			{ icon: 'pi cm-sun', name: this.translate.instant('TH_SUMMER'), value: 'summer' },
			{ icon: 'pi cm-snow', name: this.translate.instant('TH_WINTER'), value: 'winter' },
		];
		this.bnbModalityControlOptions = [
			{
				icon: 'pi cm-settings', name: this.translate.instant('MTMA_CONF_{Normal}'), value: false,
				// disabled is an extra check because we don't show at all modality if isn't supported..
				disabled: this.thermo.current_season !== 'summer' && this.thermo.current_season !== 'winter' && !this.isBnBThSupport()
			},
			{
				icon: 'pi pi-home', name: this.translate.instant('B&B'), value: true,
				// disabled is an extra check because we don't show at all modality if isn't supported..
				disabled: this.thermo.current_season !== 'summer' && this.thermo.current_season !== 'winter' && !this.isBnBThSupport()
			},
		];
		this.thermalControlOptions = [
			{
				icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF'), value: 'off',
				disabled: this.thermo.current_season === 'off' || this.thermo.current_season === 'antifreeze'
			},
			{
				icon: 'pi cm-handle-set', name: this.translate.instant('TH_MANUAL'), value: 'manual',
				disabled: this.thermo.current_season === 'off' || this.thermo.current_season === 'antifreeze'
			},
			{
				icon: 'pi cm-time', name: this.translate.instant('TH_AUTO'), value: 'auto',
				disabled: this.thermo.current_season === 'off' || this.thermo.current_season === 'antifreeze'
			},
		];
		this.bnbThermalControlOptions = [
			{
				icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF'), value: 'off',
				// disabled is an extra check because we don't show at all modality if isn't supported..
				disabled: this.thermo.mode !== 'off' && this.thermo.mode !== 'manual' && this.thermo.current_season !== 'summer' && this.thermo.current_season !== 'winter' && !this.isBnBThSupport()
			},
			{
				icon: 'pi cm-handle-set', name: this.translate.instant('TH_MANUAL'), value: 'manual',
				// disabled is an extra check because we don't show at all modality if isn't supported..
				disabled: this.thermo.mode !== 'off' && this.thermo.mode !== 'manual' && this.thermo.current_season !== 'summer' && this.thermo.current_season !== 'winter' && !this.isBnBThSupport()
			},
		];

		this.insertControlHoliday();
		this.insertControlBoost();
		this.insertControlJolly();
		this.insertControlTempManualTime();
		this.insertControlTempOffTime();
		this.insertControlAntifreeze();
	}

	insertControlHoliday(): void {
		if (this.isVisibleControlHoliday()) {
			this.thermalControlOptions.push({
				icon: 'pi cm-holiday', name: this.translate.instant('TH_HOLIDAY'), value: 'holiday',
				disabled: true
			});
		}
	}

	insertControlBoost(): void {
		if (this.isVisibleControlBoost()) {
			this.thermalControlOptions.push({
				icon: 'pi cm-setting-bolt', name: this.translate.instant('TH_BOOST'), value: 'boost',
				disabled: true
			},);
		}
	}

	insertControlJolly(): void {
		if (this.isVisibleControlJolly()) {
			this.thermalControlOptions.push({
				icon: 'pi cm-jolly', name: this.translate.instant('TH_JOLLY'), value: 'jolly',
				disabled: true
			},);
		}
	}

	insertControlTempManualTime(): void {
		if (this.isVisibleControlTempManualTime()) {
			this.thermalControlOptions.push({
				icon: 'pi cm-handle-set', name: this.translate.instant('TH_HANDLE_TIME'), value: 'temp_man',
				disabled: true
			});
		}
	}

	insertControlTempOffTime(): void {
		if (this.isVisibleControlTempOffTime()) {
			this.thermalControlOptions.push({
				icon: 'pi cm-power-off', name: this.translate.instant('TH_OFF_TIME'), value: 'temp_off',
				disabled: true
			});
		}
	}

	insertControlAntifreeze(): void {
		if (this.isVisibleControlAntifreeze()) {
			this.seasonControlOptions.push({ icon: 'pi cm-antifreeze', name: this.translate.instant('TH_ANTIFREEZE'), value: 'antifreeze' });
		}
	}

	// standard funct to send POST api metric
	updateThermo(thermoUpdate: Thermo): void {
		thermoUpdate.keycode = this.thermo.keycode;

		if (!this.isImpersMode) {
			this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate }));
		} else {
			this.store.dispatch(DeviceThActions.updateThermo({ thermo: thermoUpdate, userId: this.device.OwnerId }));
		}
	}

	thermalControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate["set_mode"] = this.f.thermalControl.value;

		this.updateThermo(thermoUpdate);
	}

	bnbControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate["set_bb.enable"] = this.f.bnbModalityControl.value;

		// if (this.thermo.mode !== 'off' && this.thermo.mode !== 'manual') {
		// 	thermoUpdate.set_mode = 'manual'; // set as default: manual
		// }

		this.updateThermo(thermoUpdate);
	}

	bnbThermalControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate["set_mode"] = this.f.bnbThermalControl.value;

		this.updateThermo(thermoUpdate);
	}

	seasonControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate["current_season"] = this.f.seasonControl.value;

		this.updateThermo(thermoUpdate);
	}

	isBnBThSupport(): boolean {
		const isThOneFwVerBnB: boolean = compareFirmwareVersions(this.thermo['Global FW Version'], BnB_MINFWVER_THONE230) >= 0;

		return this.device?.ModelId === EDeviceThermoModel.THONE
			&& isThOneFwVerBnB;
	}

	isVisibileBnBModalityControl(): boolean {
		if (!this.isBnBThSupport()) {
			return false;
		}
		return (
			this.thermo.current_season === 'summer'
			|| this.thermo.current_season === 'winter'
		)
	}

	// thermo control for THONE230 B&B activated
	// set off and manual
	isVisibileBnBThermalControl(): boolean {
		if (!this.isBnBThSupport() || this.thermo.bb?.enable !== true) {
			return false;
		}
		return (
			this.thermo.current_season === 'summer'
			|| this.thermo.current_season === 'winter'
		)
	}

	// normal thermo control (for each TH and THOneBnB in standard mode)
	// off, manual, auto etc
	isVisibileThermalControl(): boolean {
		if (this.isBnBThSupport() && this.thermo.bb?.enable === true) {
			return false;
		}
		return this.thermo.current_season !== 'off' &&
			this.thermo.current_season !== 'antifreeze'
	}

	// manual set point only for TH B&B supported with B&B mode activated
	isVisibleBnBManualControlTemperature(): boolean {
		if (!this.isBnBThSupport()) {
			return false;
		}
		// ThOne B&B doesn't have antifreeze current_season
		return this.thermo.current_season !== 'off' && this.thermo.mode === 'manual' && this.thermo.bb.enable;
	}

	// manual set point for TH not B&B supported and TH normal (standard) mode
	isVisibleManualControlTemperature(): boolean {
		if (this.isBnBThSupport()) {
			if (!this.thermo.bb.enable) { // normal mode so could be manual standard temp
				return this.thermo.current_season !== 'off' && this.thermo.mode === 'manual';
			}
			return false;
		}
		return this.thermo.current_season !== 'off' && this.thermo.current_season !== 'antifreeze' && this.thermo.mode === 'manual';
	}

	// qe enabled: courtesy time and standby temp set point
	isVisibleQnEControls(): boolean {
		if (!this.isBnBThSupport()) {
			return false;
		}
		return this.thermo.qe.enable;
	}

	isVisibleControlHoliday(): boolean {
		return this.thermo.mode === 'holiday' && (
			this.device.ModelId === EDeviceThermoModel.TH700 ||
			this.device.ModelId === EDeviceThermoModel.TH700WALL ||
			this.device.ModelId === EDeviceThermoModel.TH750WALL ||
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT
		);
	}

	isVisibleControlBoost(): boolean {
		return this.thermo.mode === 'boost' && (
			this.device.ModelId === EDeviceThermoModel.TH700 ||
			this.device.ModelId === EDeviceThermoModel.TH700WALL ||
			this.device.ModelId === EDeviceThermoModel.TH750WALL ||
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT
		);
	}

	isVisibleControlJolly(): boolean {
		return this.thermo.mode === 'jolly' && (
			this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550
		);
	}

	isVisibleControlTempManualTime(): boolean {
		return this.thermo.mode === 'temp_man' && (
			this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550 ||
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT
		);
	}

	isVisibleControlTempOffTime(): boolean {
		return this.thermo.mode === 'temp_off' && (
			this.device.ModelId === EDeviceThermoModel.THONE ||
			this.device.ModelId === EDeviceThermoModel.THONEBT
		);
	}

	isVisibleControlAntifreeze(): boolean {
		return this.thermo.mode === 'antifreeze' && (
			this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550
		);
	}

	getFlex(): string {
		if (this.device.ModelId === EDeviceThermoModel.TH500 ||
			this.device.ModelId === EDeviceThermoModel.TH550) {
			return '25%';
		}
		return '33%';
	}

	getBorderColor(): string {
		const thermo = this.thermo;
		if (!thermo || !thermo?.relay_status || thermo?.relay_status !== 1 || !thermo?.current_season) {
			return '';
		}
		const season = thermo?.current_season;
		const borderStyle = ' border-round border-2 ';
		switch (season) {
			case 'summer':
				return borderStyle + 'border-primary';
			case 'winter':
				return borderStyle + 'border-red-600';
			default:
				return '';
		}
	}

	// aux func needed for right operation
	// round the typescript float number (0.50004 etc -> 0.5)
	getValueFloat(value: number): number {
		return parseFloat(value.toFixed(1));
	}

	scaledValueFloat(value: number, scale: number): number {
		return this.getValueFloat(value * scale);
	}

	getValueString(value: number, scale: number): string {
		if (Math.floor(scale) === 1) {
			return value.toFixed(0);
		}
		return value.toFixed(1);
	}

	scaledValueString(value: number, scale: number): string {
		return this.getValueString(value * scale, scale).replace(".", ",");
	}

	formattedSliderMin(): string {
		return this.scaledValueString(0, 1);
	}

	formattedSliderMax(): string {
		return this.scaledValueString(3, 1);
	}

	// ----- DELTA BnB Settings
	setValueDelta(formControlDeltaTemp: string): void {
		// round to 0.5 closer
		let finalValue : number = Math.round(this.f[formControlDeltaTemp].value * 2) / 2;
		if (finalValue < TH_MIN_VALUE_DELTA) {
			finalValue = 0;
		}
		if (finalValue > TH_MAX_VALUE_DELTA) {
			finalValue = 3;
		}
		this.f[formControlDeltaTemp].setValue(finalValue);
		setTimeout(() => {
			this.updateThermoDeltaBnb(formControlDeltaTemp);
		}, 1500);
	}

	onChangeSlider(formControlDeltaTemp: string) {
		if (formControlDeltaTemp !== 'bnbDeltaTempPositive' && formControlDeltaTemp !== 'bnbDeltaTempNegative') {
			return;
		}
		this.setValueDelta(formControlDeltaTemp);
	}

	setSliderValue(increaseDecreaseValue: number, formControlDeltaTemp: string): void {
		if (formControlDeltaTemp !== 'bnbDeltaTempPositive' && formControlDeltaTemp !== 'bnbDeltaTempNegative') {
			return;
		}
		this.f[formControlDeltaTemp].setValue(this.f[formControlDeltaTemp].value + (increaseDecreaseValue * TH_DELTA_STEPS));
		this.setValueDelta(formControlDeltaTemp);
	}

	updateThermoDeltaBnb(formControlDeltaTemp: string): void {
		const thermoUpdate = new Thermo();
		let metric: string;
		switch (formControlDeltaTemp) {
			case 'bnbDeltaTempPositive':
				metric = 'set_bb.delta_temp_p';
				break;
			case 'bnbDeltaTempNegative':
				metric = 'set_bb.delta_temp_n';
				break;
			default:
				break;
		}

		thermoUpdate[metric] = this.thermoForm.controls[formControlDeltaTemp].value;

		this.updateThermo(thermoUpdate);
	}
	// ----- DELTA BnB Settings

	getRealTimeMinMaxTempBnB(deltaType: string): number {
		let formControlDeltaTemp: string = 'bnbDeltaTempPositive';
		if (deltaType === 'negative') {
			formControlDeltaTemp = 'bnbDeltaTempNegative';
		}
		let delta: number = deltaType === 'negative' ? this.thermo?.bb.delta_temp_n : this.thermo?.bb.delta_temp_p;
		let manualTemp: number = this.thermo?.bb.temp;
		const deltaControlValue = this.thermoForm.controls[formControlDeltaTemp].value;
		if (deltaControlValue !== undefined && deltaControlValue !== null) {
			delta = deltaControlValue;
		}
		const bnbManualTemperatureValue = this.thermoForm.controls['bnbTemperature'].value;
		if (bnbManualTemperatureValue !== undefined && bnbManualTemperatureValue !== null) {
			manualTemp = bnbManualTemperatureValue;
		}
		const result = deltaType === 'negative' ? manualTemp - delta : manualTemp + delta;
		return this.getValueFloat(result);
	}

	// ----- QE
	quickEasyEnableControlChanged(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate["set_qe.enable"] = this.f.qneControl.value;

		this.updateThermo(thermoUpdate);
	}

	updateThermoTimeCourtesy(): void {
		const thermoUpdate = new Thermo();
		thermoUpdate['set_qe.courtesy_time'] = this.thermoForm.controls['qneCourtesyTime'].value;

		this.updateThermo(thermoUpdate);
	}

	onChangeSliderTimeCourtesy() {
		this.f['qneCourtesyTime'].setValue(this.f['qneCourtesyTime'].value);
		setTimeout(() => {
			this.updateThermoTimeCourtesy();
		}, 1500);
	}

	setSliderTimeCourtesyValue(value: number): void {
		let newValue = this.f['qneCourtesyTime'].value + value * TH_QE_COURTESY_TIME_STEPS;
		newValue = this.getValueFloat(newValue);
		if (newValue < TH_QE_COURTESY_TIME_MIN_VALUE) {
			return;
		}
		if (newValue > TH_QE_COURTESY_TIME_MAX_VALUE) {
			return;
		}
		newValue = this.scaledValueFloat(newValue, 1);
		this.f['qneCourtesyTime'].setValue(newValue);
		setTimeout(() => {
			this.updateThermoTimeCourtesy();
		}, 1500);
	}

	isTimeCourtesySliderMin(): boolean {
		return this.thermoForm.controls['qneCourtesyTime'].value <= TH_QE_COURTESY_TIME_MIN_VALUE;
	}

	isTimeCourtesySliderMax(): boolean {
		return this.thermoForm.controls['qneCourtesyTime'].value >= TH_QE_COURTESY_TIME_MAX_VALUE;
	}

	// ------ STANDARD FUNCTIONS for sliders and increase, decrease buttons
	// aux for sliderTempStop() and setSliderButtonValue()
	updatingSpinnerValue(eventValue: number, formValue: FormControl, spinnerKey: string): void {
		// correcting decimal value
		const valueFixed = Number(Number(eventValue).toFixed(1));

		// updating form field and spinner value with fixed value
		formValue.setValue(valueFixed);
		(this[spinnerKey as keyof this] as unknown as number) = valueFixed;
	}

	// aux for sliderTempStop() and setSliderButtonValue()
	timerAndUpdate(timerKey: string, sliderType: SliderTemperatureSetPoint): void {
		// DEBOUNCE: catch active timer and reset it if it exists
		const activeTimer = this[timerKey as keyof this] as unknown as number;
		if (activeTimer && activeTimer !== 0) {
			window.clearTimeout(activeTimer);
		}

		// start new timer and saving on the right timer variable component
		(this[timerKey as keyof this] as unknown as number) =
			window.setTimeout(() => {
				this.updateThermoTemp(sliderType);
				(this[timerKey as keyof this] as unknown as number) = 0; // Reset del timer
			}, 1500); // waiting 1.5 secs after last click release
	}

	// UPDATE function -> POST api with updated infos
	updateThermoTemp(sliderType: SliderTemperatureSetPoint): void {
		const thermoUpdate = new Thermo();

		switch (sliderType) {
			case SliderTemperatureSetPoint.MANUAL_TEMP:
				thermoUpdate['set_manual_temp'] = this.spinnerManualTemp;
				break;
			case SliderTemperatureSetPoint.BnB_MANUAL_TEMP:
				thermoUpdate['set_bb.temp'] = this.spinnerBnB;
				break;
			case SliderTemperatureSetPoint.QnE_STANDBY_TEMP:
				thermoUpdate['set_qe.stdby_temp'] = this.spinnerQnEStandby;
				break;
			default:
				break;
		}

		this.updateThermo(thermoUpdate);
	}

	// Slider p-knob user update value
	sliderTempStop(eventValue: number, sliderType: SliderTemperatureSetPoint): void {
		let spinnerKey: string = '';
		let timerKey: string = '';
		let formValue: FormControl;

		// mapping right keys for spinner and timer
		switch (sliderType) {
			case SliderTemperatureSetPoint.MANUAL_TEMP:
				spinnerKey = 'spinnerManualTemp';
				timerKey = 'timerUpdateThermoManual';
				formValue = this.f.temperature;
				break;
			case SliderTemperatureSetPoint.BnB_MANUAL_TEMP:
				spinnerKey = 'spinnerBnB';
				timerKey = 'timerUpdateThermoBnb';
				formValue = this.f.bnbTemperature;
				break;
			case SliderTemperatureSetPoint.QnE_STANDBY_TEMP:
				spinnerKey = 'spinnerQnEStandby';
				timerKey = 'timerUpdateThermoQnE';
				formValue = this.f.qneTemperature;
				break;
			default:
				return; // we avoid execution of func with undefined values
		}

		this.updatingSpinnerValue(eventValue, formValue, spinnerKey);
		this.timerAndUpdate(timerKey, sliderType);
	}

	// Decrease and incrase buttons user update value
	setSliderButtonValue(buttonsTempSet: ButtonsTemperatureSetPoint, sliderType: SliderTemperatureSetPoint): void {
		let decrIncrValue: number = 0;
		let spinnerKey: string = '';
		let timerKey: string = '';
		let formValue: FormControl;

		// mapping right keys for spinner and timer
		switch (sliderType) {
			case SliderTemperatureSetPoint.MANUAL_TEMP:
				decrIncrValue = TH_MANUAL_TEMP_STEP;
				spinnerKey = 'spinnerManualTemp';
				timerKey = 'timerUpdateThermoManual';
				formValue = this.f.temperature;
				break;
			case SliderTemperatureSetPoint.BnB_MANUAL_TEMP:
				decrIncrValue = TH_BB_TEMP_STEP;
				spinnerKey = 'spinnerBnB';
				timerKey = 'timerUpdateThermoBnb';
				formValue = this.f.bnbTemperature;
				break;
			case SliderTemperatureSetPoint.QnE_STANDBY_TEMP:
				decrIncrValue = TH_QE_STANDBY_TEMP_STEP;
				spinnerKey = 'spinnerQnEStandby';
				timerKey = 'timerUpdateThermoQnE';
				formValue = this.f.qneTemperature;
				break;
			default:
				return; // we avoid execution of func with undefined values
		}

		// we have currentSpinner with right dynamic value spinner key
		const currentSpinnerValue = this[spinnerKey as keyof this] as unknown as number;

		let eventValue: number = 0;
		if (buttonsTempSet === ButtonsTemperatureSetPoint.INCREASE) {
			eventValue = currentSpinnerValue + decrIncrValue;
		} else { // default: decrease
			eventValue = currentSpinnerValue - decrIncrValue;
		}

		this.updatingSpinnerValue(eventValue, formValue, spinnerKey);
		this.timerAndUpdate(timerKey, sliderType);
	}

	// disable decrease or increase buttons due to reach min or max value
	isSliderMinMax(rangeCheck: string, sliderType: SliderTemperatureSetPoint): boolean {
		if (rangeCheck !== 'min' && rangeCheck !== 'max') {
			return false;
		}
		switch (sliderType) {
			case SliderTemperatureSetPoint.MANUAL_TEMP:
				return rangeCheck === 'min' ? this.f.temperature.value <= TH_MANUAL_TEMP_MIN_SETPOINT : this.f.temperature.value >= TH_MANUAL_TEMP_MAX_SETPOINT;
			case SliderTemperatureSetPoint.BnB_MANUAL_TEMP:
				return rangeCheck === 'min' ? this.f.bnbTemperature.value <= TH_BB_TEMP_MIN_SETPOINT : this.f.bnbTemperature.value >= TH_BB_TEMP_MAX_SETPOINT;
			case SliderTemperatureSetPoint.QnE_STANDBY_TEMP:
				return rangeCheck === 'min' ? this.f.qneTemperature.value <= TH_QE_STANDBY_TEMP_MIN_SETPOINT : this.f.qneTemperature.value >= TH_QE_STANDBY_TEMP_MAX_SETPOINT;
			case SliderTemperatureSetPoint.BnB_DELTA_POSITIVE:
				return rangeCheck === 'min' ? this.f.bnbDeltaTempPositive.value <= TH_MIN_VALUE_DELTA : this.f.bnbDeltaTempPositive.value >= TH_MAX_VALUE_DELTA;
			case SliderTemperatureSetPoint.BnB_DELTA_NEGATIVE:
				return rangeCheck === 'min' ? this.f.bnbDeltaTempNegative.value <= TH_MIN_VALUE_DELTA : this.f.bnbDeltaTempNegative.value >= TH_MAX_VALUE_DELTA;
			default:
				return false;
		}
	}
	// ------ STANDARD FUNCTIONS for sliders and increase, decrease buttons

	get f(): any {
		return this.thermoForm.controls;
	}

	// Design
	@HostListener('window:resize')
	onResize() {
		this.screenWidth = window.innerWidth;
	}

	onExtendedMobile(): boolean {
		return StandardPageLayout.onExtendedMobile(this.screenWidth);
	}

	underTablet(): boolean {
		return StandardPageLayout.underTablet(this.screenWidth);
	}

	col6ResponsiveNoPadding(): string {
		return StandardPageLayout.col6ResponsiveNoPadding(this.screenWidth);
	}

	dynamicInputTextFormat(classType: string): string {
		return StandardPageLayout.simpleInputTextLayout(classType, this.screenWidth);
	}
}
